/**
 * ONE chip primitive for every state family — not one component per family. Colour comes from
 * Phase 1's semantic tokens keyed off the value; words come from `domain/labels.ts`.
 */
import { cn } from "@/lib/utils";
import { type StateFamily, stateLabel } from "@/domain/labels";

export interface StateChipProps {
  family: StateFamily;
  value: string;
  size?: "sm" | "md";
  className?: string;
}

/** Full utility strings, so Tailwind sees every class it must emit. */
const TONE: Record<StateFamily, Record<string, string>> = {
  run: {
    draft: "bg-state-run-draft-bg text-state-run-draft-fg",
    preparing: "bg-state-run-preparing-bg text-state-run-preparing-fg",
    package_ready: "bg-state-run-package-ready-bg text-state-run-package-ready-fg",
    dispatched: "bg-state-run-dispatched-bg text-state-run-dispatched-fg",
    running: "bg-state-run-running-bg text-state-run-running-fg",
    succeeded: "bg-state-run-succeeded-bg text-state-run-succeeded-fg",
    failed: "bg-state-run-failed-bg text-state-run-failed-fg",
    cancelled: "bg-state-run-cancelled-bg text-state-run-cancelled-fg",
    /* The current plan is a reference treatment, not a run state — same family, distinct tone. */
    current: "bg-state-run-current-bg text-state-run-current-fg",
  },
  upload: {
    received: "bg-state-upload-received-bg text-state-upload-received-fg",
    validating: "bg-state-upload-validating-bg text-state-upload-validating-fg",
    validated: "bg-state-upload-validated-bg text-state-upload-validated-fg",
    invalid: "bg-state-upload-invalid-bg text-state-upload-invalid-fg",
    applying: "bg-state-upload-applying-bg text-state-upload-applying-fg",
    applied: "bg-state-upload-applied-bg text-state-upload-applied-fg",
    failed: "bg-state-upload-failed-bg text-state-upload-failed-fg",
  },
  delivery: {
    received: "bg-state-delivery-received-bg text-state-delivery-received-fg",
    processing: "bg-state-delivery-processing-bg text-state-delivery-processing-fg",
    processed: "bg-state-delivery-processed-bg text-state-delivery-processed-fg",
    rejected: "bg-state-delivery-rejected-bg text-state-delivery-rejected-fg",
    failed: "bg-state-delivery-failed-bg text-state-delivery-failed-fg",
  },
  outcome: {
    max_of_both: "bg-sales-max-of-both-bg text-sales-max-of-both-fg",
    orders_only: "bg-sales-orders-only-bg text-sales-orders-only-fg",
    forecast_only: "bg-sales-forecast-only-bg text-sales-forecast-only-fg",
    blocked: "bg-sales-blocked-bg text-sales-blocked-fg",
  },
  override: {
    override: "bg-sales-override-bg text-sales-override-fg",
    overtaken: "bg-sales-overtaken-bg text-sales-overtaken-fg",
  },
  publication: {
    draft: "bg-muted text-muted-foreground",
    published: "bg-state-run-succeeded-bg text-state-run-succeeded-fg",
  },
  shipmentType: {
    solved: "bg-shipment-solved-bg text-shipment-solved-fg",
    sto_fixed: "bg-shipment-sto-fixed-bg text-shipment-sto-fixed-fg",
    committed_fixed: "bg-shipment-committed-fixed-bg text-shipment-committed-fixed-fg",
    carrier_minimum: "bg-shipment-carrier-minimum-bg text-shipment-carrier-minimum-fg",
  },
  findingSeverity: {
    high: "bg-severity-high-bg text-severity-high-fg",
    medium: "bg-severity-medium-bg text-severity-medium-fg",
    low: "bg-severity-low-bg text-severity-low-fg",
  },
  disposition: {
    open: "bg-disposition-open-bg text-disposition-open-fg",
    acknowledged: "bg-disposition-acknowledged-bg text-disposition-acknowledged-fg",
    snoozed: "bg-disposition-snoozed-bg text-disposition-snoozed-fg",
  },
  auditAction: {
    create: "bg-state-upload-validated-bg text-state-upload-validated-fg",
    update: "bg-state-run-running-bg text-state-run-running-fg",
    delete: "bg-state-delivery-rejected-bg text-state-delivery-rejected-fg",
    upload: "bg-state-upload-received-bg text-state-upload-received-fg",
    feed_upsert: "bg-muted text-muted-foreground",
  },
};

export function StateChip({ family, value, size = "md", className }: StateChipProps) {
  const tone = TONE[family][value] ?? "bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center truncate rounded-[var(--radius-sm)] font-medium whitespace-nowrap",
        size === "sm" ? "type-caption px-1.5 py-0" : "type-caption px-2 py-0.5",
        tone,
        className,
      )}
    >
      {stateLabel(family, value)}
    </span>
  );
}
